import { Form, Formik } from 'formik';

import { useEffect, useMemo, useRef, useState } from 'react';

import styles from './party-headers.module.scss';
import { GetContractDetails } from '@/graphql/contracts/_generated_/getContractDetails.query';
import { validationSchema } from './validationSchema';
import { ContractCommonData } from '@/graphql/contracts/_generated_/ContractCommonData.fragment';
import useSearchCustomers from '@/pages/AddContract/hooks/useSearchCustomers';
import PartyColumn from './PartyColumn';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import useUpdateShipToCustomer from '../../hooks/useUpdateShipToCustomer';
import useUpdateInvoiceToCustomer from '../../hooks/useUpdateInvoiceToCustomer';
import useUpdateContractCustomer from '../../hooks/useUpdateContractCustomer';
import { UserRole } from '@/config/roles.config';

import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import ContractPartiesModal from './PartyHeaderModal';

interface IHeadersProps {
  isLoadingContractDetails?: boolean;
  contract: GetContractDetails['contract'] | undefined;
}

export interface IPartyFormValues {
  contractedTo: ContractCommonData['customer'] | undefined | null;
  shipToCustomer: ContractCommonData['customer'] | undefined | null;
  invoiceToCustomer: ContractCommonData['customer'] | undefined | null;
}

export default function PartyHeaders({
  isLoadingContractDetails = false,
  contract,
}: IHeadersProps) {
  const searchContractedTo = useSearchCustomers();
  const searchShipTo = useSearchCustomers();
  const searchInvoiceTo = useSearchCustomers();

  const contractedToRef = useRef<any>(null);
  const shipToRef = useRef<any>(null);
  const invoiceToRef = useRef<any>(null);

  const { updateContractCustomer } = useUpdateContractCustomer();
  const { updateShipToCustomer } = useUpdateShipToCustomer();
  const { updateInvoiceToCustomer } = useUpdateInvoiceToCustomer();

  const [userRole, setUserRole] = useState<UserRole>();
  const { getUserRole } = useGetUserRole();
  const [showModal, setShowModal] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState<IPartyFormValues | null>(null);
  const allowedRole = (
    userRole === UserRole.FULL_ACCESS || 
    userRole === UserRole.ADMIN ||
    userRole === UserRole.USER);
  useEffect(() => {
    const getRole = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };

    getRole();
  }, [getUserRole]);

  const isDisabledEditField = useMemo(() => {
    return (
      isLoadingContractDetails ||
      Boolean(contract?.oneEvoMigrated) ||
      contract?.status?.status === 'Cancelled' ||
      contract?.status?.status === 'Expired' ||
      contract?.status?.status === 'Inactive' ||
      contract?.status?.status === 'Terminated'
    );
  }, [isLoadingContractDetails, contract?.status?.status, contract?.oneEvoMigrated]);

  const normalizeId = (id: string | undefined | null): string | undefined => {
    if (!id || id === '') {
      return undefined;
    }
    return id as string;
  };

  function getNormalizedIdsAndChanged(
    oldId: string | undefined | null,
    newId: string | undefined | null
  ): {
      normalizedOldId: string | undefined;
      normalizedNewId: string | undefined;
      changed: boolean;
    } {
    const normalizedOldId = normalizeId(oldId);
    const normalizedNewId = normalizeId(newId);
    return {
      normalizedOldId,
      normalizedNewId,
      changed: normalizedOldId !== normalizedNewId,
    };
  }

  const handleContractedToUpdate = async (
    oldId: string | undefined | null,
    newId: string | undefined | null
  ) => {
    const { normalizedNewId, changed } = getNormalizedIdsAndChanged(oldId, newId);

    if (
      !isLoadingContractDetails && contract && contract.customer && changed && normalizedNewId) {
      await updateContractCustomer({
        contractId: contract.contractID,
        currentCustomerId: oldId!,
        newCustomerId: normalizedNewId,
      });
      contractedToRef?.current?.setValue(undefined);
    }
  };

  const handleShipToCustomerUpdate = async (
    oldId: string | undefined | null,
    newId: string | undefined | null,
    defaultShipTo: boolean,
  ) => {
    const { normalizedNewId, changed } = getNormalizedIdsAndChanged(
      oldId,
      newId
    );

    if (!isLoadingContractDetails && contract && changed && normalizedNewId) {
      await updateShipToCustomer({
        contractId: contract.contractID,
        shipToCustomerId: normalizedNewId,
        defaultToLines: defaultShipTo
      });
      shipToRef?.current?.setValue(undefined);
    }
  };

  const handleInvoiceToCustomerUpdate = async (
    oldId: string | undefined | null,
    newId: string | undefined | null,
    defaultInvoiceTo: boolean,
  ) => {
    const { normalizedNewId, changed } = getNormalizedIdsAndChanged(
      oldId,
      newId
    );

    if (!isLoadingContractDetails && contract && changed && normalizedNewId) {
      await updateInvoiceToCustomer({
        contractId: contract.contractID,
        invoiceToCustomerId: normalizedNewId,
        defaultToLines: defaultInvoiceTo
      });
      invoiceToRef?.current?.setValue(undefined);
    }
  };

  const onSubmit = async (values: IPartyFormValues, defaultShipTo: boolean, defaultInvoiceTo: boolean) => {
    await handleContractedToUpdate(
      contract?.customer?.customerID,
      values.contractedTo?.customerID
    );
    await handleShipToCustomerUpdate(
      contract?.shipToCustomer?.customerID,
      values.shipToCustomer?.customerID,
      defaultShipTo
    );
    await handleInvoiceToCustomerUpdate(
      contract?.invoiceToCustomer?.customerID,
      values.invoiceToCustomer?.customerID,
      defaultInvoiceTo
    );
  };

  const handleSaveClick = (values: IPartyFormValues) => {
    setPendingFormValues(values);
    setShowModal(true);
  };

  const handleModalContinue = async (shipToChecked: boolean, invoiceToChecked: boolean) => {
    if (pendingFormValues) {
      await onSubmit(pendingFormValues, shipToChecked, invoiceToChecked);
    }
    setShowModal(false);
    setPendingFormValues(null);
  };

  const handleModalCancel = () => {
    setShowModal(false);
    setPendingFormValues(null);
  };

  const isButtonDisabled = (values: IPartyFormValues) =>
    !(
      getNormalizedIdsAndChanged(
        contract?.customer?.customerID,
        values.contractedTo?.customerID
      ).changed ||
      getNormalizedIdsAndChanged(
        contract?.shipToCustomer?.customerID,
        values.shipToCustomer?.customerID
      ).changed ||
      getNormalizedIdsAndChanged(
        contract?.invoiceToCustomer?.customerID,
        values.invoiceToCustomer?.customerID
      ).changed
    );

  return (
    <>
      <Formik
        initialValues={{
          contractedTo: contract?.customer,
          shipToCustomer: contract?.shipToCustomer,
          invoiceToCustomer: contract?.invoiceToCustomer,
        }}
        enableReinitialize={true}
        onSubmit={async (values) => {
          handleSaveClick(values);
        }}
        validationSchema={validationSchema}
        validateOnMount={true}
      >
        {({ errors, touched, values, setFieldValue, setFieldTouched }) => (
          <Form>
            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: isLoadingContractDetails ? '650px' : '1000px',
                  gap: '20px',
                }}
              >
                <PartyColumn
                  ref={contractedToRef}
                  fieldName="Contracted To"
                  fieldKey="contractedTo"
                  customerParty={contract?.customer}
                  isEditable={allowedRole && !isDisabledEditField}
                  isRequired={false}
                  touched={touched}
                  errors={errors}
                  setFieldTouched={setFieldTouched}
                  setFieldValue={setFieldValue}
                  searchCus={searchContractedTo}
                  isLoadingContractDetails={isLoadingContractDetails}
                />
                <>
                    <PartyColumn
                      ref={shipToRef}
                      fieldName="Default Ship To"
                      fieldKey="shipToCustomer"
                      customerParty={contract?.shipToCustomer}
                      isEditable={!isDisabledEditField}
                      isRequired={false}
                      touched={touched}
                      errors={errors}
                      setFieldTouched={setFieldTouched}
                      setFieldValue={setFieldValue}
                      searchCus={searchShipTo}
                      isLoadingContractDetails={isLoadingContractDetails}
                    />

                    <PartyColumn
                      ref={invoiceToRef}
                      fieldName="Default Invoice To"
                      fieldKey="invoiceToCustomer"
                      customerParty={contract?.invoiceToCustomer}
                      isEditable={!isDisabledEditField}
                      isRequired={false}
                      touched={touched}
                      errors={errors}
                      setFieldTouched={setFieldTouched}
                      setFieldValue={setFieldValue}
                      searchCus={searchInvoiceTo}
                      isLoadingContractDetails={isLoadingContractDetails}
                    />
                  </>
              </div>
              <div className={styles['party-header-save-btn']}>
                <Button
                  type="submit"
                  buttonStyleType={ButtonStyleType.PRIMARY}
                  disabled={isButtonDisabled(values)}
                >
                  Save
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      {showModal && pendingFormValues && (
        <ContractPartiesModal
          isOpen={showModal}
          onClose={handleModalCancel}
          onContinue={handleModalContinue}
          contract={contract}
          formValues={pendingFormValues}
          getNormalizedIdsAndChanged={getNormalizedIdsAndChanged}
        />
      )}
    </>
  );
}
